import { yupResolver } from '@hookform/resolvers/yup';
import { Button, IconButton, IGuestInfo, useConfirmContext } from '@localina/core';
import { TrashIcon } from '@localina/icons';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { GuestInfo } from '../GuestInfo';
import { useGuestSchema } from './guest-schema';

interface IProps {
    guest?: IGuestInfo;
    onSave: (guest: IGuestInfo) => void;
    onClose: () => void;
    onDelete: (guest: IGuestInfo) => void;
}

export interface IGuestViewHandle {
    requestClose: () => void;
}

export const GuestView = forwardRef<IGuestViewHandle, IProps>((props, ref) => {
    const { t } = useTranslation();
    const confirmContext = useConfirmContext();

    const guestInfoSchema = useGuestSchema();
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(yup.object().shape(guestInfoSchema)),
        defaultValues: {
            guestStatus: props.guest?.guestStatus || null,
            restaurantComment: props.guest?.restaurantComment,
            emailRequired: Boolean(props.guest?.email),
            email: props.guest?.email || '',
            firstName: props.guest?.firstName || '',
            lastName: props.guest?.lastName || '',
            salutation: props.guest?.salutation,
            company: props.guest?.company || '',
            allergies: props.guest?.allergies,
            phoneNumbers: props.guest?.phoneNumbers.map((phone) => ({ phone })) || [],
        },
    });

    const { formState } = methods;
    const { isValid, isDirty } = formState;

    const handleClose = async () => {
        if (
            !isDirty ||
            (await confirmContext.confirm({ msg: t('guest.details.view.dialog.confirmDiscard.title') })) === 'yes'
        ) {
            props.onClose();
        }
    };
    const handleSave = async () => {
        if (props.guest && isValid && confirmContext) {
            const values = methods.getValues();
            if ((await confirmContext.confirm({ msg: t('guest.details.view.dialog.save.message') })) === 'yes') {
                props.onSave({
                    allergies: values.allergies,
                    guestStatus: values.guestStatus,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    company: values.company,
                    restaurantComment: values.restaurantComment,
                    salutation: values.salutation || undefined,
                    phoneNumbers: values.phoneNumbers.map(({ phone }) => phone),
                    id: props.guest.id,
                    subscribeToNewsletter: props.guest.subscribeToNewsletter,
                });
            }
        }
    };

    const handleDelete = async () => {
        if (
            props.guest &&
            confirmContext &&
            (await confirmContext.confirm({ msg: t('guest.details.view.dialog.message') })) === 'yes'
        ) {
            props.onDelete(props.guest);
        }
    };

    useImperativeHandle(ref, () => ({ requestClose: handleClose }), [handleClose]);

    useEffect(() => {
        if (props.guest) {
            methods.reset({
                guestStatus: props.guest?.guestStatus || null,
                restaurantComment: props.guest?.restaurantComment,
                emailRequired: Boolean(props.guest?.email),
                email: props.guest?.email || '',
                firstName: props.guest?.firstName || '',
                lastName: props.guest?.lastName || '',
                salutation: props.guest?.salutation,
                company: props.guest?.company || '',
                allergies: props.guest?.allergies,
                phoneNumbers: props.guest?.phoneNumbers.map((phone) => ({ phone })) || [],
            });
        }
    }, [props.guest]);

    if (!props.guest) {
        return null;
    }

    return (
        <div className="guest-view">
            <div className="guest-view__body">
                <FormProvider {...methods}>
                    <form>
                        <div className="group">
                            <GuestInfo guestInfo={props.guest} />
                        </div>
                    </form>
                </FormProvider>
            </div>
            {}
            <div className="guest-view__footer">
                <div className="buttons">
                    <IconButton icon={<TrashIcon size="big" />} onClick={handleDelete} />
                    <Button label={t('common.buttons.cancel')} secondary={true} onClick={handleClose} />
                    <Button label={t('common.buttons.save')} disabled={!isValid || !isDirty} onClick={handleSave} />
                </div>
            </div>
        </div>
    );
});

GuestView.displayName = 'GuestView';
